import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Button,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Cancel,
  HourglassEmpty,
  Group,
  Person,
  School,
  Category,
} from '@mui/icons-material';
import adminService from '../../../services/admin.service';

interface GroupRequest {
  requestId: string;
  requesterFullname: string;
  requesterProfileImage: string;
  requesterSchoolName: string;
  groupName: string;
  groupDescription: string;
  groupCategory: string;
  estimatedMemberCount: number;
  requestReason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  createdAt: string;
  rejectionReason?: string;
}

const GroupRequestList: React.FC = () => {
  const [requests, setRequests] = useState<GroupRequest[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState({ pending: 0, approved: 0, rejected: 0 });

  // Dialog states
  const [selectedRequest, setSelectedRequest] = useState<GroupRequest | null>(null);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const statusMap = ['PENDING', 'APPROVED', 'REJECTED', 'ALL'];

  useEffect(() => {
    fetchRequests();
  }, [activeTab, search, page]);

  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await adminService.getAllGroupRequests({
        page,
        limit: 12,
        status: statusMap[activeTab],
        search,
      });

      if (response.status) {
        setRequests(response.data.requests || []);
        setStats(response.data.stats || { pending: 0, approved: 0, rejected: 0 });
        setTotalPages(response.data.pagination?.totalPages || 1);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Talepler yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (requestId: string) => {
    setActionLoading(true);
    try {
      await adminService.approveGroupRequest(requestId);
      alert('Grup talebi onaylandı ve grup oluşturuldu!');
      await fetchRequests();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Onaylama sırasında hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selectedRequest || !rejectionReason.trim()) return;

    setActionLoading(true);
    try {
      await adminService.rejectGroupRequest(selectedRequest.requestId, rejectionReason);
      alert('Grup talebi reddedildi!');
      setRejectDialog(false);
      setRejectionReason('');
      await fetchRequests();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Reddetme sırasında hata oluştu.');
    } finally {
      setActionLoading(false);
    }
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      ACADEMIC: '#667eea',
      SOCIAL: '#f093fb',
      SPORTS: '#4facfe',
      ARTS: '#fa709a',
      TECHNOLOGY: '#43e97b',
      OTHER: '#a8edea',
    };
    return colors[category] || '#666';
  };

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      ACADEMIC: 'Akademik',
      SOCIAL: 'Sosyal',
      SPORTS: 'Spor',
      ARTS: 'Sanat',
      TECHNOLOGY: 'Teknoloji',
      OTHER: 'Diğer',
    };
    return labels[category] || category;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        Grup Talepleri
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Kullanıcıların grup oluşturma taleplerini inceleyin ve onaylayın.
      </Typography>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Bekleyen
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {stats.pending}
                </Typography>
              </Box>
              <HourglassEmpty sx={{ fontSize: 40, color: '#ff9800' }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Onaylanan
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {stats.approved}
                </Typography>
              </Box>
              <CheckCircle sx={{ fontSize: 40, color: '#4caf50' }} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #e0e0e0' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Reddedilen
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {stats.rejected}
                </Typography>
              </Box>
              <Cancel sx={{ fontSize: 40, color: '#f44336' }} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={0} sx={{ mb: 3, borderRadius: 3, border: '1px solid #e0e0e0' }}>
        <Tabs
          value={activeTab}
          onChange={(_, newValue) => {
            setActiveTab(newValue);
            setPage(1);
          }}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Bekleyen" />
          <Tab label="Onaylanan" />
          <Tab label="Reddedilen" />
          <Tab label="Tümü" />
        </Tabs>

        <Box sx={{ p: 2 }}>
          <TextField
            fullWidth
            placeholder="Grup adı, talep sahibi veya requestId ile ara..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Requests Grid */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : requests.length === 0 ? (
        <Paper elevation={0} sx={{ p: 8, textAlign: 'center', borderRadius: 3, border: '1px solid #e0e0e0' }}>
          <Typography color="text.secondary">Talep bulunamadı.</Typography>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3}>
            {requests.map((request) => (
              <Grid item xs={12} md={6} lg={4} key={request.requestId}>
                <Card elevation={0} sx={{ height: '100%', borderRadius: 3, border: '1px solid #e0e0e0' }}>
                  <CardContent>
                    {/* Status Badge */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      {request.status === 'PENDING' && (
                        <Chip icon={<HourglassEmpty />} label="Bekliyor" color="warning" size="small" />
                      )}
                      {request.status === 'APPROVED' && (
                        <Chip icon={<CheckCircle />} label="Onaylandı" color="success" size="small" />
                      )}
                      {request.status === 'REJECTED' && (
                        <Chip icon={<Cancel />} label="Reddedildi" color="error" size="small" />
                      )}
                      <Chip
                        label={getCategoryLabel(request.groupCategory)}
                        size="small"
                        sx={{
                          backgroundColor: `${getCategoryColor(request.groupCategory)}20`,
                          color: getCategoryColor(request.groupCategory),
                        }}
                      />
                    </Box>

                    {/* Group Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                      <Avatar sx={{ width: 56, height: 56, bgcolor: getCategoryColor(request.groupCategory) }}>
                        <Group />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="h6" fontWeight="bold" noWrap>
                          {request.groupName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(request.createdAt).toLocaleDateString('tr-TR')}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {request.groupDescription}
                    </Typography>

                    {/* Requester Info */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Avatar src={request.requesterProfileImage} sx={{ width: 24, height: 24 }}>
                        {request.requesterFullname[0]}
                      </Avatar>
                      <Typography variant="body2" noWrap>
                        {request.requesterFullname}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <School sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {request.requesterSchoolName}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Person sx={{ fontSize: 18, color: 'text.secondary' }} />
                      <Typography variant="caption" color="text.secondary">
                        Tahmini {request.estimatedMemberCount} üye
                      </Typography>
                    </Box>

                    {request.rejectionReason && (
                      <Alert severity="error" sx={{ mt: 2 }}>
                        <Typography variant="caption">
                          <strong>Red Sebebi:</strong> {request.rejectionReason}
                        </Typography>
                      </Alert>
                    )}
                  </CardContent>

                  {request.status === 'PENDING' && (
                    <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                      <Button
                        size="small"
                        color="error"
                        onClick={() => {
                          setSelectedRequest(request);
                          setRejectDialog(true);
                        }}
                        disabled={actionLoading}
                      >
                        Reddet
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleApprove(request.requestId)}
                        disabled={actionLoading}
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        }}
                      >
                        Onayla
                      </Button>
                    </CardActions>
                  )}
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Grup Talebini Reddet</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Lütfen red sebebini açıklayın. Bu mesaj talep sahibine gönderilecektir.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Red Sebebi"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>İptal</Button>
          <Button
            onClick={handleReject}
            color="error"
            variant="contained"
            disabled={!rejectionReason.trim() || actionLoading}
          >
            {actionLoading ? <CircularProgress size={24} /> : 'Reddet'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GroupRequestList;

